import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { PRODUCTS, ProductId } from '../lib/stripe-config';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

export function useStripe() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const createCheckoutSession = async (productId: ProductId) => {
    if (!user) {
      toast.error('Please sign in to continue');
      return;
    }

    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session found');
      }

      const product = PRODUCTS[productId];
      const origin = window.location.origin;

      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          price_id: product.priceId,
          success_url: `${origin}/settings?tab=account&checkout=success`,
          cancel_url: `${origin}/pricing`,
          mode: product.mode,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();

      if (!url) {
        throw new Error('No checkout URL received');
      }

      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      if (error instanceof Error && error.message === 'No active session found') {
        toast.error('Please sign in again to continue');
        navigate('/login');
      } else {
        toast.error('Failed to start checkout process');
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    createCheckoutSession,
  };
}